/**
 * How an ASN's status should read — CR-7142.
 *
 * A wholly-direct load is acknowledged but deliberately stays at status
 * `submitted`: it ships supplier -> customer and never reaches Saltney or Mold,
 * so moving it to `in_transit` would promise an arrival that never comes. That
 * leaves the stored status unable to say whether the load has been
 * acknowledged — it reads "Submitted" either way, which is precisely the
 * confusion this resolves.
 *
 * So the badge shows a derived status instead. Kept in one place because the
 * ASN list and the ASN detail modal each render their own badge, and two copies
 * of this rule would eventually disagree about the same ASN.
 */

/** The parts of an ASN this needs — satisfied by both the list and detail shapes. */
export interface AsnStatusSource {
  status: string;
  /** Derived on the backend from the delivery lines (`is_direct_delivery`). */
  is_direct_delivery?: boolean;
  customer_acknowledgment_date?: string | null;
}

/**
 * The status key to render for this ASN.
 *
 * Returns `'acknowledged'` for a direct load the customer has acknowledged;
 * otherwise the stored status, unchanged. Warehouse loads are never affected —
 * acknowledging one moves it to `in_transit`, which already says what happened.
 *
 * @param asn - The ASN to describe.
 * @returns A status key for the badge lookup.
 */
export function asnDisplayStatus(asn: AsnStatusSource): string {
  if (
    asn.is_direct_delivery &&
    asn.status === 'submitted' &&
    asn.customer_acknowledgment_date
  ) {
    return 'acknowledged';
  }
  return asn.status;
}
